import { Classes, H6, Intent, Tag } from "@blueprintjs/core";
import { Form, Input } from "decorator-form";
import React from "react";
import { addEnum, Entity, Field } from "typegraph";

import CountResult from "common/entities/CountResult";
import { ID } from "common/entities/IEntity";
import Lifecycle from "common/entities/Lifecycle";
import InputType from "common/form/InputType";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { EntityTable } from "core/components/EntityTable";
import DateLabel from "core/components/label/DateLabel";
import { iDef } from "core/util/TypeUtils";
import entityMeta from "panels/kb/entityMeta";
import { articleStatusSelect } from "panels/kb/util/widget";
import constants from "../../constants";

/**
 * admin article list
 */

@Entity(entityMeta.ARTICLE)
@Form()
class Article {
	@Field()
	public id: ID;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Name",
		description: "Internal Name (unique)",
		required: true
	})
	public name: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Display Name",
		description: "End user-facing name"
	})
	public displayName: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Keywords"
	})
	public keywords: string;

	@Field()
	@Input({ type: InputType.TEXT_HTML, title: "Description" })
	public description: string;

	@Field()
	public iconUrl: string;

	@Input({ type: InputType.UPLOAD_URL_IMAGE, title: "Icon" })
	public tempIconUrl: string;

	@Field()
	@Input({
		type: articleStatusSelect,
		title: "Status"
	})
	public status: string;

	@Field()
	public createdOn: string;

	@Field()
	public publishedOn: string;
}

function mapArticleQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

class ArticleQuery implements IPageableListQuery<Article> {
	@Field({
		entity: Article,
		params: mapArticleQueryParams,
		aliasFor: "article_many"
	})
	public entities: null | Article[];

	@Field({
		entity: CountResult,
		params: mapArticleQueryParams,
		aliasFor: "article_count"
	})
	public count: CountResult;
}

interface IArticleListProps {
	knowledgeBaseId?: ID;
	categoryId?: ID;
	includeDeleted?: boolean;
}

export default function ArticleList(props: IArticleListProps) {
	const params = {};

	if (props.categoryId) {
		params["categoryIds"] = [props.categoryId];
	}

	if (props.knowledgeBaseId) {
		params["knowledgeBaseId"] = props.knowledgeBaseId;
	}

	const statuses = [
		addEnum(Lifecycle.Available),
		addEnum(Lifecycle.Predeployed),
		addEnum(Lifecycle.Published),
		addEnum(Lifecycle.UnderConstruction)
	];

	if (props.includeDeleted) {
		statuses.push(addEnum(Lifecycle.Deleted));
	}

	params["statuses"] = statuses;

	return (
		<EntityTable
			pagingQuery={ArticleQuery}
			formClass={Article}
			params={params}
			editParams={{ statuses }}
			getInitialCreateData={() => ({ status: Lifecycle.Predeployed })}
			columns={[
				{
					key: "title",
					label: "Title",
					sort: "displayTitle",
					renderCell: ({ data }) => (
						<PageLinkView
							viewKey="article.link"
							preserveParams={false}
							params={{
								[constants.PARAM_ARTICLE_ID]: data.id,
								[constants.PARAM_KB_ID]: props.knowledgeBaseId
							}}
						>
							<H6>{data.displayName || data.name}</H6>
							<p
								className={
									Classes.TEXT_MUTED +
									" " +
									Classes.TEXT_SMALL
								}
							>
								{data.name}
							</p>
						</PageLinkView>
					)
				},
				{
					key: "createdOn",
					label: "Created",
					sort: "createdOn",
					renderCell: ({ data }) => (
						<DateLabel value={data.createdOn} />
					)
				},
				{
					key: "publishedOn",
					label: "Published",
					sort: "publishedOn",
					renderCell: ({ data }) => (
						<DateLabel value={data.publishedOn} />
					)
				},
				{
					key: "status",
					label: "Status",
					sort: "status",
					renderCell: ({ data }) => {
						return (
							<Tag
								intent={
									data.status === "Deleted"
										? Intent.DANGER
										: data.status === "Predeployed"
										? Intent.WARNING
										: Intent.PRIMARY
								}
								minimal={true}
							>
								{data.status}
							</Tag>
						);
					}
				}
			]}
			onAfterRefetch={article => {
				const copy = { ...article };

				// avoid react-jsonschema-form type check

				if (!article.displayName) {
					copy.displayName = "";
				}

				if (!article.keywords) {
					copy.keywords = "";
				}

				if (!article.description) {
					copy.description = "";
				}

				return copy;
			}}
			onBeforeSave={article => {
				const actionParams = {};

				if (article.id) {
					actionParams["id"] = article.id;
				} else {
					actionParams["knowledgeBaseId"] = props.knowledgeBaseId;

					if (props.categoryId) {
						actionParams["categoryIds"] = [props.categoryId];
					}
				}

				iDef(article.name, name => {
					actionParams["name"] = name;
				});

				iDef(article.displayName, displayName => {
					actionParams["displayName"] = displayName;
				});

				iDef(article.description, description => {
					actionParams["description"] = description;
				});

				if (
					!!article.tempIconUrl &&
					article.iconUrl !== article.tempIconUrl
				) {
					actionParams["iconUrl"] = article.tempIconUrl;
				}

				if (article.status) {
					actionParams["status"] = addEnum(article.status);
				}

				return actionParams;
			}}
		/>
	);
}



// WEBPACK FOOTER //
// ./src/panels/kb/article/admin/ArticleList.tsx